import React, { useState, useEffect, useRef } from 'react'
import {
  LayoutDashboard,
  Zap,
  Package,
  Users,
  Tag,
  Hash,
  Layers,
  HelpCircle
} from 'lucide-react'
import { useNavigationTracking } from '../../contexts/AnalyticsContext'
import { ActivationsList, ActivationDashboard } from '../activations'
// TODO: Planner feature to be revisited in future release
// import { PlannerTab } from '../planner'
// import { Activation } from '../../types/activation'
import { AssetsList } from '../assets'
import {
  AttributionTagsList,
  DiscoveredHashtagsList,
  TagFormDrawer,
  DiscoveredHashtagInsightsDrawer,
  AttributionTagInsightsDrawer,
  PromoteHashtagDrawer
} from '../attribution-tags'
import {
  ActivationTypesList,
  AddActivationTypeDrawer,
  EditActivationTypeDrawer
} from '../activation-types'
import { ActivationType } from '@/types/activation'
import { PartnersList } from '../partners'
import { useAttributionTagStore } from '@/stores/attributionTagStore'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import usePartnerStore from '@/stores/partnerStore'
import useAssetStore from '@/stores/assetStore'
import useActivationStore from '@/stores/activationStore'
import useActivationDashboardStore from '@/stores/activationDashboardStore'
import { DiscoveredHashtag } from '@/types/attributionTag'
// Content Performance relocated to the Social dashboard (2026-07-15):
// import ContentPerformancePage from '../content-performance/ContentPerformancePage'
import CommercializationRoadmapModal from './CommercializationRoadmapModal'
import { useCommercializationStatus } from '../../hooks/useCommercializationStatus'

type SponsorshipTab = 'dashboard' | 'activations' | 'assets' | 'attribution-tags' | 'discovered-tags' | 'partners' | 'content-performance'
type ActivationsSubTab = 'activations' | 'activation-types'

// Tag data type from AttributionTagsTab
interface TagData {
  hashtagId: number
  hashtag: string
  status: string
  postCount: number
  totalViews: number
  totalLikes: number
  totalComments: number
  totalShares: number
  totalEmv: string
  totalEmvValue: number
  previousEmvValue: number
  accountCount: number
}

const SponsorshipsPage: React.FC = () => {
  const { trackTabChanged } = useNavigationTracking()
  const [activeTab, setActiveTab] = useState<SponsorshipTab>('dashboard')
  // TODO: Planner feature to be revisited in future release
  // const [editingActivation, setEditingActivation] = useState<Activation | null>(null)
  const [showRoadmapModal, setShowRoadmapModal] = useState(false)

  // Commercialization roadmap status
  const {
    isFirstTimeUser,
    isLoading: isCommercializationLoading,
    hasPartners,
    hasAttributionTags,
    hasAssets,
    hasActivations,
    dismissed,
    dismissRoadmap
  } = useCommercializationStatus()

  const [activationsSubTab, setActivationsSubTab] = useState<ActivationsSubTab>('activations')

  // Attribution tags drawer state (moved from AttributionTagsTab)
  const [promoteHashtag, setPromoteHashtag] = useState<DiscoveredHashtag | null>(null)

  // Activation types drawer state
  const [isActivationTypeAddDrawerOpen, setIsActivationTypeAddDrawerOpen] = useState(false)
  const [isActivationTypeEditDrawerOpen, setIsActivationTypeEditDrawerOpen] = useState(false)
  const [selectedActivationType, setSelectedActivationType] = useState<ActivationType | null>(null)
  const [selectedAttributionTagForDrawer, setSelectedAttributionTagForDrawer] = useState<{
    hashtagId: number
    hashtag: string
    period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom'
    customDateRange?: { start: string; end: string } | null
  } | null>(null)
  const [selectedDiscoveredHashtagForDrawer, setSelectedDiscoveredHashtagForDrawer] = useState<{
    hashtagId: number
    hashtag: string
    period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom'
    customDateRange?: { start: string; end: string } | null
  } | null>(null)

  const { selectedCustomer } = useGlobalCustomerStore()
  const previousCustomerIdRef = useRef<string | null>(null)

  const {
    isCreateDrawerOpen,
    isEditDrawerOpen,
    selectedTag,
    openCreateDrawer,
    closeCreateDrawer,
    closeEditDrawer,
    openEditDrawer,
    loadTagBasicDetail,
    loadAttributionTagsAnalytics,
    loadDiscoveredHashtagsAnalytics,
    reset: resetAttributionTags
  } = useAttributionTagStore()

  const { reset: resetPartners } = usePartnerStore()
  const { reset: resetAssets } = useAssetStore()
  const { reset: resetActivations } = useActivationStore()
  const { reset: resetDashboard } = useActivationDashboardStore()

  // Refetch data when customer ID changes
  useEffect(() => {
    const currentCustomerId = selectedCustomer?.id || null

    if (previousCustomerIdRef.current !== null && currentCustomerId !== previousCustomerIdRef.current) {
      // Reset all stores when customer changes
      resetAttributionTags()
      resetPartners()
      resetAssets()
      resetActivations()
      resetDashboard()

      if (currentCustomerId) {
        loadAttributionTagsAnalytics()
        loadDiscoveredHashtagsAnalytics()
      }
      setSelectedAttributionTagForDrawer(null)
      setSelectedDiscoveredHashtagForDrawer(null)
      setPromoteHashtag(null)
    }

    previousCustomerIdRef.current = currentCustomerId
  }, [selectedCustomer?.id, resetAttributionTags, resetPartners, resetAssets, resetActivations, resetDashboard, loadAttributionTagsAnalytics, loadDiscoveredHashtagsAnalytics])

  // Auto-show roadmap modal for first-time users
  useEffect(() => {
    if (!isCommercializationLoading && isFirstTimeUser && !dismissed) {
      setShowRoadmapModal(true)
    }
  }, [isCommercializationLoading, isFirstTimeUser, dismissed])

  const handleTabChange = (newTab: SponsorshipTab) => {
    trackTabChanged(newTab, activeTab)
    setActiveTab(newTab)
    // TODO: Planner feature to be revisited in future release
    // Clear editing activation when switching away from activations tab
    // if (newTab !== 'activations') {
    //   setEditingActivation(null)
    // }
  }

  const handleActivationsSubTabChange = (newSubTab: ActivationsSubTab) => {
    trackTabChanged(`activations-${newSubTab}`, `activations-${activationsSubTab}`)
    setActivationsSubTab(newSubTab)
  }

  // Attribution tag handlers
  const handleAttributionTagSelect = (
    hashtagId: number,
    hashtag: string,
    period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom',
    customDateRange?: { start: string; end: string } | null
  ) => {
    setSelectedAttributionTagForDrawer({ hashtagId, hashtag, period, customDateRange })
  }

  const handleDiscoveredHashtagSelect = (
    hashtagId: number,
    hashtag: string,
    period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom',
    customDateRange?: { start: string; end: string } | null
  ) => {
    setSelectedDiscoveredHashtagForDrawer({ hashtagId, hashtag, period, customDateRange })
  }

  const handleCreateClick = () => {
    openCreateDrawer()
  }

  const handleEditClick = (tag: TagData) => {
    loadTagBasicDetail(tag.hashtagId)
    openEditDrawer()
  }

  const handleEditFromInsights = (hashtagId: number) => {
    setSelectedAttributionTagForDrawer(null)
    loadTagBasicDetail(hashtagId)
    openEditDrawer()
  }

  const handleCloseEditDrawer = () => {
    closeEditDrawer()
  }

  const handleCreateSuccess = () => {
    loadAttributionTagsAnalytics()
  }

  const handleEditSuccess = () => {
    loadAttributionTagsAnalytics()
  }

  const handlePromoteClick = (hashtag: DiscoveredHashtag) => {
    setPromoteHashtag(hashtag)
  }

  const handlePromoteSuccess = () => {
    setPromoteHashtag(null)
    // Switch to attribution tags tab to show the newly promoted tag
    setActiveTab('attribution-tags')
  }

  const handlePromoteFromInsightsDrawer = (hashtag: DiscoveredHashtag) => {
    setSelectedDiscoveredHashtagForDrawer(null)
    setPromoteHashtag(hashtag)
  }

  // Roadmap modal handlers
  const handleRoadmapNavigate = (tab: string) => {
    setShowRoadmapModal(false)
    setActiveTab(tab as SponsorshipTab)
  }

  const handleRoadmapClose = () => {
    setShowRoadmapModal(false)
    dismissRoadmap()
  }

  return (
    <div className='min-h-screen bg-gray-50/50'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Header */}
        <div className='mb-6'>
          <div className='flex items-center gap-2'>
            <h1 className='text-2xl font-bold text-gray-900 tracking-tight'>
              Sponsorships
            </h1>
            <button
              onClick={() => setShowRoadmapModal(true)}
              className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'
              title='Getting Started Guide'
            >
              <HelpCircle className='h-5 w-5' />
            </button>
          </div>
          <p className='text-sm text-gray-600 mt-1'>
            Manage sponsorship campaigns, activations, inventory, and sponsors
          </p>
        </div>

        {/* Main Tab Navigation */}
        <div className='px-2 pt-2 pb-0'>
          <div className='flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner'>
            <button
              onClick={() => handleTabChange('dashboard')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'dashboard'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <LayoutDashboard className='h-3.5 w-3.5 mr-1.5' />
              Dashboard
            </button>
            <button
              onClick={() => handleTabChange('activations')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'activations'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Zap className='h-3.5 w-3.5 mr-1.5' />
              Activations
            </button>
            <button
              onClick={() => handleTabChange('assets')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'assets'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Package className='h-3.5 w-3.5 mr-1.5' />
              Assets
            </button>
            <button
              onClick={() => handleTabChange('attribution-tags')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'attribution-tags'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Tag className='h-3.5 w-3.5 mr-1.5' />
              Attribution Tags
            </button>
            <button
              onClick={() => handleTabChange('discovered-tags')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'discovered-tags'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Hash className='h-3.5 w-3.5 mr-1.5' />
              Discovered Tags
            </button>
            <button
              onClick={() => handleTabChange('partners')}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
                activeTab === 'partners'
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <Users className='h-3.5 w-3.5 mr-1.5' />
              Sponsors
            </button>
            {/* Content Performance relocated to the Social dashboard (2026-07-15). */}
          </div>
        </div>

        {/* Activations sub-navigation (Activations | Activation Types) */}
        {activeTab === 'activations' && (
          <div className='flex items-center gap-2 p-1 bg-gray-100 rounded-lg w-fit mt-4 ml-2'>
            <button
              onClick={() => handleActivationsSubTabChange('activations')}
              className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activationsSubTab === 'activations'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Zap className='h-3.5 w-3.5 mr-1.5' />
              Activations
            </button>
            <button
              onClick={() => handleActivationsSubTabChange('activation-types')}
              className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activationsSubTab === 'activation-types'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Layers className='h-3.5 w-3.5 mr-1.5' />
              Activation Types
            </button>
          </div>
        )}

        {/* Content Area */}
        <div className='bg-white rounded-lg shadow-sm p-8 mt-4'>
          {activeTab === 'dashboard' && <ActivationDashboard />}

          {/* Activations Tab Content (sub-tabs: Activations | Activation Types) */}
          {activeTab === 'activations' && activationsSubTab === 'activations' && <ActivationsList />}
          {activeTab === 'activations' && activationsSubTab === 'activation-types' && (
            <ActivationTypesList
              onCreateClick={() => setIsActivationTypeAddDrawerOpen(true)}
              onEditClick={(type) => {
                setSelectedActivationType(type)
                setIsActivationTypeEditDrawerOpen(true)
              }}
            />
          )}

          {/* Assets Tab Content */}
          {activeTab === 'assets' && <AssetsList />}

          {/* Attribution Tags Tab Content */}
          {activeTab === 'attribution-tags' && (
            <AttributionTagsList
              onCreateClick={handleCreateClick}
              onEditClick={handleEditClick}
              onTagSelect={handleAttributionTagSelect}
            />
          )}

          {/* Discovered Tags Tab Content */}
          {activeTab === 'discovered-tags' && (
            <DiscoveredHashtagsList
              onPromoteClick={handlePromoteClick}
              onHashtagSelect={handleDiscoveredHashtagSelect}
            />
          )}

          {/* Partners Tab Content */}
          {activeTab === 'partners' && <PartnersList />}

          {/* Content Performance relocated to the Social dashboard (2026-07-15). */}
        </div>
      </div>

      {/* Attribution Tag Drawers */}
      <TagFormDrawer
        isOpen={isCreateDrawerOpen}
        mode='create'
        onClose={closeCreateDrawer}
        onSuccess={handleCreateSuccess}
      />

      <TagFormDrawer
        isOpen={isEditDrawerOpen}
        mode='edit'
        tag={selectedTag}
        onClose={handleCloseEditDrawer}
        onSuccess={handleEditSuccess}
      />

      <AttributionTagInsightsDrawer
        isOpen={selectedAttributionTagForDrawer !== null}
        onClose={() => setSelectedAttributionTagForDrawer(null)}
        hashtagId={selectedAttributionTagForDrawer?.hashtagId ?? null}
        hashtag={selectedAttributionTagForDrawer?.hashtag ?? null}
        period={selectedAttributionTagForDrawer?.period ?? '90days'}
        customDateRange={selectedAttributionTagForDrawer?.customDateRange}
        onEditClick={handleEditFromInsights}
      />

      <DiscoveredHashtagInsightsDrawer
        isOpen={selectedDiscoveredHashtagForDrawer !== null}
        onClose={() => setSelectedDiscoveredHashtagForDrawer(null)}
        hashtagId={selectedDiscoveredHashtagForDrawer?.hashtagId ?? null}
        hashtag={selectedDiscoveredHashtagForDrawer?.hashtag ?? null}
        period={selectedDiscoveredHashtagForDrawer?.period ?? '90days'}
        customDateRange={selectedDiscoveredHashtagForDrawer?.customDateRange}
        onPromoteClick={handlePromoteFromInsightsDrawer}
      />

      <PromoteHashtagDrawer
        isOpen={promoteHashtag !== null}
        hashtag={promoteHashtag}
        onClose={() => setPromoteHashtag(null)}
        onSuccess={handlePromoteSuccess}
      />

      {/* Activation Type Drawers */}
      <AddActivationTypeDrawer
        isOpen={isActivationTypeAddDrawerOpen}
        onClose={() => setIsActivationTypeAddDrawerOpen(false)}
      />

      <EditActivationTypeDrawer
        isOpen={isActivationTypeEditDrawerOpen}
        onClose={() => {
          setIsActivationTypeEditDrawerOpen(false)
          setSelectedActivationType(null)
        }}
        activationType={selectedActivationType}
      />

      {/* Commercialization Roadmap Modal */}
      <CommercializationRoadmapModal
        isOpen={showRoadmapModal}
        onClose={handleRoadmapClose}
        onNavigate={handleRoadmapNavigate}
        hasPartners={hasPartners}
        hasAttributionTags={hasAttributionTags}
        hasAssets={hasAssets}
        hasActivations={hasActivations}
      />
    </div>
  )
}

export default SponsorshipsPage
